import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Zap,
  Shield,
  Settings,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { BrandIcons } from '../../ui/brand-icons';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ZapierSuccessModalContent } from './ZapierSuccessModal';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { ContentFiltersFields } from '../shared/ContentFiltersFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface ZapierIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
}

export default function ZapierIntegrationModal({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId
}: ZapierIntegrationModalProps) {
  
  const { t } = useTranslation();
  
  // Form state
  const [integrationName, setIntegrationName] = useState('Zapier Automation');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.zapier);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);
  
  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);
  
  // Filter states
  const [categories, setCategories] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [minImportance, setMinImportance] = useState(1);
  const [requestsPerHour, setRequestsPerHour] = useState(10000);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationName.trim() || !selectedSocket) return;

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: categories,
        excludeTagsComma: excludeTags,
        minImportance,
        requestsPerHour,
      });

      const auth_config = {
        description: description.trim() || 'Zapier automation platform integration',
        response_format: 'json'
      };

      const integrationData = {
        integration_type: 'zapier',
        integration_name: integrationName.trim(),
        permissions,
        auth_config,
        socket_id: selectedSocket.socket_id
      };

      const createdIntegration = await onCreateIntegration(integrationData);
      
      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        binding_id: createdIntegration.binding_id,
        auth_config: {
          ...integrationData.auth_config,
          api_key: createdIntegration.auth_config.api_key
        },
        space_id: createdIntegration.space_id,
        permissions: integrationData.permissions
      });
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Failed to create Zapier integration:', error);
      toast.error(t('zapierIntegration.messages.creationFailed', { ns: 'integrations' }) as string);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);
    
    // Reset form
    setIntegrationName('Zapier Automation');
    setDescription('');
    setCanRead(true);
    setCanWrite(true);
    setCanSearch(true);
    setCanDelete(false);
    setCategories('');
    setExcludeTags('');
    setMinImportance(1);
    setRequestsPerHour(10000);
    
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        {showSuccessModal && createdIntegration ? (
          <ZapierSuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
          />
        ) : (
          <>
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-3">
            <BrandIcons.zapier className="w-8 h-8" />
            {t('zapierIntegration.connectTitle', { ns: 'integrations' }) as string}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {selectedSocket ? (
              t('zapierIntegration.connectDescription', { ns: 'integrations' }) as string
            ) : (
              'Setting up your integration...'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogScrollArea className="space-y-6 px-6">
          {/* Installation Overview */}
          <Card className="border-orange-200 bg-orange-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                <Zap className="w-5 h-5" />
                Quick Setup Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-orange-200">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-700">1</div>
                  <div>
                    <div className="font-medium text-gray-900">Create Integration</div>
                    <div className="text-sm text-gray-600">Complete this form</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-orange-200">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-700">2</div>
                  <div>
                    <div className="font-medium text-gray-900">Get API Key</div>
                    <div className="text-sm text-gray-600">Copy credentials</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-orange-200">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-700">3</div>
                  <div>
                    <div className="font-medium text-gray-900">Connect Zapier</div>
                    <div className="text-sm text-gray-600">Add to your Zaps</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Integration Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Basic Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Integration Name
                  </label>
                  <Input
                    value={integrationName}
                    onChange={(e) => setIntegrationName(e.target.value)}
                    placeholder="e.g., Zapier Automation"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Description (Optional)
                  </label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., Main Zapier workflow integration"
                    className="w-full"
                  />
                </div>

                <RateLimitFields
                  requestsPerHour={requestsPerHour}
                  onRequestsPerHourChange={setRequestsPerHour}
                  min={100}
                  max={100000}
                />
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Security & Permissions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <MemoryPermissionsFields
                  variant="embedded"
                  layout="inline"
                  value={{
                    can_read: canRead,
                    can_write: canWrite,
                    can_search: canSearch,
                    can_delete: canDelete,
                  }}
                  onChange={(v) => {
                    setCanRead(v.can_read);
                    setCanWrite(v.can_write);
                    setCanSearch(v.can_search);
                    setCanDelete(v.can_delete);
                  }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Advanced Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Advanced Filters (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ContentFiltersFields
                variant="embedded"
                categories={categories}
                excludeTags={excludeTags}
                minImportance={minImportance}
                onCategoriesChange={setCategories}
                onExcludeTagsChange={setExcludeTags}
                onMinImportanceChange={setMinImportance}
              />
            </CardContent>
          </Card>

          {/* Advanced Socket Selection */}
          {spaceId && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <AdvancedSocketSelection
                integration={integrationRegistry.zapier}
                spaceId={spaceId}
                currentSocket={selectedSocket}
                onSocketChange={setSelectedSocket}
                disabled={isCreating || socketLoading}
              />
              
              {/* Socket Loading State */}
              {socketLoading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="text-blue-800 text-sm">Setting up socket...</p>
                  </div>
                </div>
              )}

              {/* Socket Error State */}
              {socketError && !socketLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-red-800 text-sm font-medium">Failed to load socket</p>
                      <p className="text-red-700 text-xs mt-1">{socketError}</p>
                      <p className="text-red-700 text-xs mt-1">You can create a socket manually in the advanced section above.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          </DialogScrollArea>
          <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={!integrationName.trim() || isCreating || !selectedSocket || socketLoading}
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Create Zapier Integration
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
